import {
	includedPropList,
	includedT2PropList,
} from "../../../pages/activityBuilder/modules/forestManagement/forestManagement";
import type {
	ForestDisturbanceCreatePayload,
	ForestDisturbanceData,
	ForestDisturbanceDeletePayload,
	ForestDisturbancePayload,
	ForestDisturbanceResponse,
	ForestManagementPayload,
	ForestManagementResponse,
	ForestManagementT2,
	ModuleFull,
} from "../../../pages/activityBuilder/modules/forestManagement/forestManagementTypes";
import {
	convertIncomingPercentage,
	convertOutgoingPercentage,
	convertEmptyStringsToNull,
	filterObjectProps
} from "../../../pages/activityBuilder/modules/inputs/utils";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createEntriesGetApi, createModuleGetApi } from "./factories/createModuleApi";

const MODULE_TYPE = "ForestManagement";
const TAG: TagTypes = "ForestManagement";
const TAG_DEFAULTS: TagTypes = "ForestManagementDefaults";
const ENDPOINT = FEModulesObj[FEModules.ForestManagement].path;
const percentageProps = [
	"rotation_percentage_biomass_for_energy",
	"logging_percentage_agb_logged",
	"logging_percentage_biomass_for_energy",
	"average_yearly_degradation_percentage",
];

const transformForestManagementResponse = (response: ForestManagementResponse) => {
	if (response) {
		const module = filterObjectProps(response, includedPropList) as ForestManagementPayload;
		const t2 = filterObjectProps(response, includedT2PropList) as ForestManagementT2;

		convertIncomingPercentage(module, percentageProps);
		return { parent: module, tiertwo: t2, disturbances: [] };
	}
	return null;
}

const forestManagementApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse: transformForestManagementResponse,
});

const publicForestManagementApiSlice = createModuleGetApi<ModuleFull>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
	transformResponse: transformForestManagementResponse,
});

export const { useGetForestManagementQuery, useGetForestManagementDefaultsQuery } = forestManagementApiSlice;
export const {
	useGetForestManagementQuery: usePublicGetForestManagementQuery,
	useGetForestManagementDefaultsQuery: usePublicGetForestManagementDefaultsQuery
} = publicForestManagementApiSlice;

const ENTRY_MODULE_TYPE = "ForestDisturbances";
const TAG_ENTRY: TagTypes = "ForestDisturbance";
export const TAG_ENTRY_DEFAULTS: TagTypes = "ForestDisturbanceDefaults";
export const ENTRY_ENDPOINT = "forest-disturbances";

const transformForestDisturbanceResponse = (response: ForestDisturbanceData[]) => {
	if (response && response.length > 0) {
		for (const res of response)
			convertIncomingPercentage(res, ["percentage_biomass_destruction"])

		return response;
	}
	return [];
}

const forestDisturbanceApiSlice = createEntriesGetApi<ForestDisturbanceData[]>({
	apiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformForestDisturbanceResponse
});

const publicForestDisturbanceApiSlice = createEntriesGetApi<ForestDisturbanceData[]>({
	apiSlice: publicApiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
	transformResponse: transformForestDisturbanceResponse
});

export const { useGetForestDisturbancesQuery, useGetForestDisturbancesDefaultsQuery } = forestDisturbanceApiSlice;
export const {
	useGetForestDisturbancesQuery: usePublicGetForestDisturbancesQuery,
	useGetForestDisturbancesDefaultsQuery: usePublicGetForestDisturbancesDefaultsQuery
} = publicForestDisturbanceApiSlice;

export const moduleApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		updateForestManagement: builder.mutation<
			ForestManagementResponse,
			ModuleFull
		>({
			query({ parent, tiertwo, activityId }) {
				const lucId = parent.land_use_change;

				convertOutgoingPercentage(parent, [
					"rotation_percentage_biomass_for_energy",
					"logging_percentage_agb_logged",
					"logging_percentage_biomass_for_energy",
					"average_yearly_degradation_percentage",
				]);

				const payload = {
					...parent,
					...tiertwo,
				} as unknown as ForestManagementResponse;
				const { id, ...rest } = payload;
				const body = { ...rest, land_use_change: lucId, activity: activityId };
				convertEmptyStringsToNull(body);
				return { url: `forest-managements/${id}/`, method: "PUT", body };
			},
			invalidatesTags: (_results, _error, { activityId, parent }) => [
				{ type: TAG, id: parent.id ?? undefined },
				{ type: "ModuleResults", id: parent.id ? `forest-managements-${parent.id}` : undefined },
				{ type: "ModuleHistory", id: parent.id ? `forest-managements-${parent.id}` : undefined },
				{ type: TAG_DEFAULTS, id: parent.id ?? undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModulesStatuses", id: activityId ?? undefined }
			],
		}),
		updateForestDisturbance: builder.mutation<
			ForestDisturbanceResponse,
			ForestDisturbancePayload
		>({
			query: ({ body }) => {
				const { id, ...rest } = body;
				const forestDisturbance = { ...rest };

				convertOutgoingPercentage(forestDisturbance, [
					"percentage_biomass_destruction",
				]);

				convertEmptyStringsToNull(forestDisturbance);
				return {
					url: `forest-disturbances/${id}/`,
					method: "PUT",
					body: forestDisturbance,
				};
			},
			invalidatesTags: (_results, _error, data) => [
				{ type: TAG_ENTRY, id: data.activityId },
				{ type: "ModuleResults", id: data.body.forest_management ? `forest-managements-${data.body.forest_management}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModuleHistory", id: data.body.forest_management ? `forest-managements-${data.body.forest_management}` : undefined },
				{ type: TAG_ENTRY_DEFAULTS, id: data.body.id ? data.body.forest_management : undefined },
				{ type: "ModulesStatuses", id: data.activityId }
			],
		}),
		createForestDisturbance: builder.mutation<
			ForestDisturbanceResponse,
			ForestDisturbanceCreatePayload
		>({
			query: ({ body }) => ({
				url: "forest-disturbances/",
				method: "POST",
				body,
			}),
			invalidatesTags: (_results, _error, data) => [
				{ type: TAG_ENTRY, id: data.activityId },
				{ type: "ModuleResults", id: data.body.parent ? `forest-managements-${data.body.parent}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModuleHistory", id: data.body.parent ? `forest-managements-${data.body.parent}` : undefined },
				{ type: "ModulesStatuses", id: data.activityId },
			],
		}),
		deleteForestDisturbance: builder.mutation<
			ForestDisturbanceResponse,
			ForestDisturbanceDeletePayload
		>({
			query: ({ id }) => ({
				url: `forest-disturbances/${id}/`,
				method: "DELETE",
			}),
			invalidatesTags: (_results, _error, data) => [
				{ type: TAG_ENTRY, id: data.activityId },
				{ type: "ModuleResults", id: data.parent ? `forest-managements-${data.parent}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModuleHistory", id: data.parent ? `forest-managements-${data.parent}` : undefined },
				{ type: "ModulesStatuses", id: data.activityId },
			],
		}),
	}),
});

export const {
	useUpdateForestManagementMutation,
	useUpdateForestDisturbanceMutation,
	useCreateForestDisturbanceMutation,
	useDeleteForestDisturbanceMutation,
} = moduleApiSlice;
